/* eslint-disable @typescript-eslint/no-unused-vars */
import SearchIcon from '@/assets/SVGs/icons/SearchIcon';
import { cn } from '@/lib/utils';
import { IDataTableProps } from '@/types';
import { PropsWithChildren, useCallback } from 'react';
import { Skeleton } from '@/components/ui/Skeleton';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import AppButton from './AppButton';
import AppInput from './AppInput';
import AppPagination from './AppPagination';
import { Plus, Search } from 'lucide-react';

function AppTable({
  header = [],
  data = [],
  theme: _theme = 'default',
  hasPagination = false,
  hasMore: _hasMore = false,
  totalCount: _totalCount = 0,
  itemsPerPage: _itemsPerPage = 10,
  currentPage = 1,
  handleClick = () => {},
  handleCellClick = () => {},
  handlePageChange: _handlePageChange = () => {},
  handlePages = () => {},
  handleLoadDataCount: _handleLoadDataCount = () => {},
  handleSearch = () => {},
  isLoading = false,
  className = '',
  totalPages = 0,
  disablePagination: _disablePagination = false,
  showFixedNumber: _showFixedNumber = true,
  tableButtonContent = '',
  modalRef
}: IDataTableProps) {
  const handleShow = useCallback(() => {
    modalRef?.showModal();
  }, [modalRef]);

  return (
    <div className="flex flex-col">
      <TableUpperSection
        handleShow={handleShow}
        handleSearch={handleSearch}
        title={''}
        tableButtonContent={tableButtonContent}
      />
      <div className={cn('flex min-h-96 flex-col justify-between', className)}>
        {/* Mobile: card view */}
        <div className="block sm:hidden">
          {isLoading ? (
            <div className="space-y-3 p-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="rounded-lg border border-coal-200 bg-white p-4">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4 rounded" />
                    <Skeleton className="h-3 w-1/2 rounded" />
                    <Skeleton className="h-3 w-2/3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className="flex h-72 items-center justify-center">
              <p className="text-sm text-coal-400">No data to show</p>
            </div>
          ) : (
            <div className="space-y-2 p-1">
              {data.map((rowData, rowIndex) => (
                <div
                  key={rowIndex}
                  onClick={() => handleClick(rowData)}
                  className="rounded-lg border border-coal-200 bg-white p-4 transition-colors hover:border-primary/30 hover:bg-primary-50/30"
                >
                  <div className="space-y-2">
                    {header.map(item => (
                      <div
                        key={item.name}
                        className="flex items-center justify-between gap-2"
                        onClick={() => handleCellClick(rowData, item)}
                      >
                        <span className="text-xs font-medium uppercase tracking-wider text-coal-400">
                          {item.name}
                        </span>
                        <span className="text-sm text-coal-700">
                          {rowData[item.key]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop: table view */}
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-coal-200">
                {header &&
                  header.map(item => (
                    <th
                      key={item.name}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-coal-400"
                    >
                      {item.name}
                    </th>
                  ))}
              </tr>
            </thead>
            {isLoading ? (
              <tbody>
                {Array.from({ length: 10 }).map((_, index) => (
                  <tr key={index} className="border-b border-coal-100">
                    {header.map((_, itemIndex) => (
                      <td key={itemIndex} className="px-4 py-3.5">
                        <Skeleton className="h-5 w-full rounded" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              <>
                <tbody>
                  {data.length > 0 &&
                    data.map((rowData, rowIndex) => (
                      <tr
                        onClick={() => handleClick(rowData)}
                        key={rowIndex}
                        className="border-b border-coal-100 transition-colors hover:bg-coal-100/40"
                      >
                        {header &&
                          header.map(item => (
                            <TableCell
                              onClick={() => handleCellClick(rowData, item)}
                              key={item.name}
                            >
                              {rowData[item.key]}
                            </TableCell>
                          ))}
                      </tr>
                    ))}
                  {data.length == 0 && (
                    <tr>
                      <TableCell
                        colSpan={header.length}
                        className="h-72 text-center text-sm text-coal-400"
                      >
                        No data to show
                      </TableCell>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </div>
        {hasPagination && (
          <div className="border-t border-coal-100 pt-2">
            <AppPagination
              isLoading={isLoading}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePages}
            />
          </div>
        )}
      </div>
    </div>
  );
}
export default AppTable;

interface ITableCellProps extends PropsWithChildren {
  className?: string;
  colSpan?: number;
  onClick?: () => void;
}
function TableCell({ children, className, ...props }: ITableCellProps) {
  return (
    <td
      onClick={props.onClick}
      className={cn('px-4 py-3.5 text-sm capitalize text-coal-700', className)}
      {...props}
    >
      {children}
    </td>
  );
}

function TableUpperSection({
  title,
  handleShow,
  tableButtonContent,
  handleSearch,
}: {
  title?: string;
  handleShow?: () => void;
  tableButtonContent?: string;
  handleSearch?: (term: string) => void;
}) {
  const { register, handleSubmit } = useForm();
  const searchParams: SubmitHandler<FieldValues> = values => {
    handleSearch!(values.search);
  };
  return (
    <section className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {title && <h2 className="text-base font-semibold text-coal-900">{title}</h2>}
      <div className="flex flex-1 items-center justify-between gap-3 sm:justify-end">
        <form onSubmit={handleSubmit(searchParams)} className="w-full sm:w-auto">
          <AppInput
            className="bg-coal-100/50 border-coal-200 sm:w-64"
            {...register('search')}
            placeholder="Search..."
            icon={Search}
          />
        </form>
        {tableButtonContent && (
          <AppButton onClick={handleShow} size="sm" icon={Plus}>
            {tableButtonContent}
          </AppButton>
        )}
      </div>
    </section>
  );
}
